import { css } from '@emotion/react'
import { zodResolver } from '@hookform/resolvers/zod'
import { InputTextControlled } from '@nexans/react-form'
import { Button, ServiceLogo, Stack, theme, Typography } from '@nexans/react-ui-kit'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { ROUTER_PUBLIC_LINKS } from '@/router/config'
import { useAppStore } from '@/stores/appStore'
import { PublicDto } from '@/types'

import { useSigninUser } from '../api/useSigninUser'
import { SigninUserSchema } from '../schemas'

import { AuthFooter } from './AuthFooter'
import { AuthOtpState } from './CodeVerification'

export function Signin() {
  const { t } = useTranslation(['auth', 'common', 'errors'])
  const navigate = useNavigate()
  const setSessionId = useAppStore((state) => state.actions.setSessionId)
  const {
    control,
    formState: { errors, isValid },
    getValues,
    handleSubmit,
  } = useForm<PublicDto['SigninUserCommand']>({
    resolver: zodResolver(SigninUserSchema),
    mode: 'onChange',
  })

  const { mutate: signinUser, isPending } = useSigninUser({
    config: {
      onSuccess: (data) => {
        setSessionId(data.sessionId)
        const otpState: AuthOtpState = { email: getValues('email'), origin: 'signin' }
        navigate(ROUTER_PUBLIC_LINKS.authOtp.path, { state: otpState })
      },
    },
  })

  const handleUserKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && isValid && !isPending) {
      handleSubmit(onSubmit)()
    }
  }

  const onSubmit = (data: PublicDto['SigninUserCommand']) => {
    signinUser(data)
  }

  return (
    <Stack align="center" justify="space-between" spacing="lg" fullHeight fullWidth>
      <ServiceLogo css={{ alignSelf: 'center', marginTop: 24 }} iconSrc={undefined} iconSrcScale={1} size={40} title="Mobiway™" />

      <Stack spacing="lg">
        <Typography css={{ marginBottom: theme.spacings['2xl'] }} textAlign="center" variant="h2">
          {t('auth:labels.signin.title')}
        </Typography>

        <InputTextControlled
          control={control}
          css={{ minWidth: 400 }}
          errorText={errors.email?.message ? t(`errors:${errors.email?.message}`) : ''}
          gutterBottom={!!errors.email}
          label={t('auth:labels.register.email')}
          name="email"
          fullWidth
          required
          onKeyDown={handleUserKeyPress}
        />
        <Button
          color="gradient"
          css={styles.gradientButton}
          disabled={!isValid || isPending}
          variant="contained"
          onClick={handleSubmit(onSubmit)}
        >
          {t('auth:actions.signin')}
        </Button>

        <Stack direction="vertical" mt="3xl" spacing="xl" fullWidth>
          <div css={styles.divider}>
            <Typography textAlign="center" variant="subtitle1">
              {t('auth:labels.signin.no_account')}
            </Typography>
          </div>
          <Button
            color="primary"
            css={{ backgroundColor: '#EEEEEE' }}
            disabled={isPending}
            variant="text"
            onClick={() => navigate(ROUTER_PUBLIC_LINKS.authRegister.path)}
          >
            {t('auth:actions.create_account')}
          </Button>
        </Stack>
      </Stack>

      <AuthFooter />
    </Stack>
  )
}

const styles = {
  gradientButton: css({
    '&:not(:disabled)': { filter: 'drop-shadow(0 16px 32px rgba(255, 0, 0, 0.35))' },
  }),
  divider: css({
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginTop: theme.spacings['xl'],
    width: '100%',
    '&::before': {
      content: '""',
      flex: 1,
      height: 1,
      backgroundColor: theme.colors.gray[200],
    },
    '&::after': {
      content: '""',
      flex: 1,
      height: 1,
      backgroundColor: theme.colors.gray[200],
    },
  }),
}
